// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByNodeId, getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { MappedLink } from "@insite/mobius/Menu";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import AccountMessagesCount, {
    AccountMessagesCountPresentationProps,
} from "@store/Library/Components/Toolkit/AccountMessagesCount";
import { boxStyles } from "@store/Themes/common/mixins/box";
import { linkStyles } from "@store/Themes/common/mixins/link";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { pxToRem, pxToRemArray } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { spacingStyles } from "@toolkit/Themes/common/mixins";
import * as React from "react";
import { createRef, useMemo } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

/* Store Custom Code - Start */
import LogOut from "@store/Library/Components/Toolkit/Icons/LogOut";
/* Store Custom Code - End */

export interface SideMenuPresentationProps extends ExpandStyles<SideMenuStyles> {}

export type SideMenuLinkPresentationProps = LinkPresentationProps & {
    isCurrent?: boolean;
};

export interface SideMenuStyles {
    wrapper?: InjectableCss;
    skipNav?: SkipNavStyles;
    list?: InjectableCss;
    listItem?: InjectableCss;
    link?: SideMenuLinkPresentationProps;
    signOutLink?: SideMenuLinkPresentationProps;
    accountMessagesCountStyles?: AccountMessagesCountPresentationProps;
}

const baseLinkStyles = (
    color = "text.linkAlt",
    hoverColor: string | null = "text.linkAltHover",
    underline = false,
    hoverUnderline = true,
) => css<SideMenuLinkPresentationProps>`
    ${linkStyles(color, hoverColor, underline, hoverUnderline)}
    width: 100%;
    padding: ${pxToRem(11, 18)};
    border-left: ${pxToRem(4)} solid transparent;
    border-right: ${pxToRem(4)} solid transparent;

    ${({ theme }) => css`
        outline-offset: -${theme.focus.width};
    `}
`;

export const sideMenuStyles: SideMenuStyles = {
    wrapper: {
        css: css`
            ${spacingStyles()}
            ${boxStyles(null)}
            background-color: ${getColor("common.background")};
            color: inherit;
        `,
    },
    list: {
        css: css`
            font-size: ${x => x.theme.design.typography.size.smaller};
            line-height: ${x => x.theme.design.typography.lineHeight.small};
            padding: ${pxToRem(11, 0)};
        `,
    },
    listItem: {},
    link: {
        css: css`
            ${baseLinkStyles()}

            ${({ isCurrent, theme }) => {
                if (isCurrent) {
                    return css`
                        font-weight: ${theme.design.typography.weight.bold};
                        border-left-color: ${getColor("tertiary")}; /* Store Custom Code */

                        &:hover {
                            text-decoration: none;
                        }
                    `;
                }
            }}
        `,
    },
    signOutLink: {
        css: css<SideMenuLinkPresentationProps>`
            ${baseLinkStyles("text.link", "text.linkHover")}
            font-weight: ${x => x.theme.design.typography.weight.bold};
            color: ${getColor("text")}; /* Store Custom Code */
        `,
        /* Store Custom Code - Start */
        icon: {
            position: "right",
            iconProps: {
                src: LogOut,
                color: "primary",
                css: css`
                    margin-left: ${pxToRem(10)};
                `,
            },
        },
        /* Store Custom Code - End */
    },
    accountMessagesCountStyles: { position: pxToRemArray([0, 4]) },
};

const List = styled.ul`
    ${injectCss}
`;
const ListItem = styled.li`
    ${injectCss}
`;
const StyledLink = styled(Link)<SideMenuLinkPresentationProps>``;

export interface SideMenuProps extends SideMenuPresentationProps {
    pageType?: string;
    nodeId?: string;
    showRootPage?: boolean;
    showSignOut?: boolean;
    onClick?: React.EventHandler<React.MouseEvent>;
    excludedPageTypes?: string[];
    links?: (MappedLink | PageLinkModel)[];
}

const mapStateToProps = (state: ApplicationState, props: SideMenuProps) => ({
    rootPageLink: props.pageType
        ? getPageLinkByPageType(state, props.pageType)
        : getPageLinkByNodeId(state, props.nodeId || getCurrentPage(state).nodeId),
    currentLocation: getLocation(state),
    isSignedIn: state.context.session.userName && !getCurrentUserIsGuest(state),
});

const mapDispatchToProps = {
    signOut,
};

type Props = SideMenuProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const SideMenu: React.FunctionComponent<Props> = ({
    showRootPage,
    showSignOut,
    overridedStyles,
    extendedStyles,
    onClick,
    rootPageLink,
    currentLocation,
    isSignedIn,
    signOut,
    excludedPageTypes = [],
    links: baseLinks,
}) => {
    const styles = useExpandProps(sideMenuStyles, extendedStyles, overridedStyles);
    const afterMenu = createRef<HTMLSpanElement>();
    const links = (baseLinks?.length ? baseLinks : rootPageLink?.children) as
        | (MappedLink & PageLinkModel)[]
        | undefined;

    const linkStyles = useMemo(() => {
        return mergeProps<SideMenuLinkPresentationProps>(styles.link, {
            typographyProps: {
                css: css`
                    position: relative;
                    display: flex;
                    justify-content: space-between;
                    width: 100%;
                `,
            },
        });
    }, [styles.link]);

    if (!links?.length && !showSignOut) {
        return null;
    }

    const currentPageUrl = currentLocation.pathname.toLowerCase();
    const _excludedPageTypes = [...excludedPageTypes];

    const _links = links?.filter(x => !x.excludeFromNavigation && !_excludedPageTypes.some(y => x.type === y));

    return (
        <StyledWrapper as="nav" {...styles.wrapper}>
            <SkipNav destination={afterMenu} extendedStyles={styles.skipNav} text={translate("Skip Navigation")} />
            <List {...styles.list}>
                {showRootPage && rootPageLink && (
                    <ListItem {...styles.listItem}>
                        <StyledLink
                            href={rootPageLink.url}
                            isCurrent={currentPageUrl === rootPageLink.url.toLowerCase()}
                            onClick={onClick}
                            {...linkStyles}
                        >
                            {rootPageLink.title}
                            <AccountMessagesCount {...styles.accountMessagesCountStyles} />
                        </StyledLink>
                    </ListItem>
                )}

                {_links?.map((link, index) => (
                    <ListItem key={link.id || `${link.title}_${index}`} {...styles.listItem}>
                        <StyledLink
                            href={link.url}
                            isCurrent={Boolean(link.url) && currentPageUrl.startsWith(link.url.toLowerCase())}
                            onClick={(event: React.MouseEvent) => {
                                link.clickableProps?.onClick?.(event);
                                onClick?.(event);
                            }}
                            icon={{ iconProps: link.iconProps }}
                            {...styles.link}
                        >
                            {link.title}
                        </StyledLink>
                    </ListItem>
                ))}

                {isSignedIn && showSignOut && (
                    <ListItem {...styles.listItem}>
                        <StyledLink
                            onClick={(event: React.MouseEvent) => {
                                signOut();
                                onClick?.(event);
                            }}
                            {...(styles.signOutLink ? styles.signOutLink : styles.link)}
                        >
                            {translate("Sign Out")}
                        </StyledLink>
                    </ListItem>
                )}
            </List>
            <span ref={afterMenu} tabIndex={-1} />
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(SideMenu);
